'use client';

interface PolicyItem {
  title: string;
  content: string | string[];
}

interface RefundCancellationSectionProps {
  title: string;
  items: PolicyItem[];
}

export default function RefundCancellationSection({ title, items }: RefundCancellationSectionProps) {
  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-headline font-bold text-foreground">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground font-body leading-relaxed">
            We want you to feel confident in your investment. Our flexible policies ensure you have options if our program isn't the right fit.
          </p>
        </div>
        
        <div className="space-y-8">
          {items.map((item, index) => (
            <div
              key={index}
              className="bg-background rounded-xl p-8 shadow-sky-sm hover:shadow-sky-md transition-all duration-300 border border-border/50"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-gradient-to-br from-accent to-primary">
                    <span className="text-sm font-headline font-bold text-primary-foreground">
                      {index + 1}
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-headline font-bold text-foreground mb-4">
                    {item.title}
                  </h3>
                  {Array.isArray(item.content) ? (
                    <ul className="space-y-3">
                      {item.content.map((point, idx) => (
                        <li key={idx} className="flex gap-3 text-muted-foreground font-body leading-relaxed">
                          <span className="text-accent font-bold mt-1">▸</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground font-body leading-relaxed">
                      {item.content}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-accent/10 border border-accent/20 rounded-xl">
            <h4 className="font-headline font-bold text-foreground mb-2">Quick Refund Timeline</h4>
            <p className="text-sm text-muted-foreground font-body">
              Refund requests processed within 7-10 business days to your original payment source.
            </p>
          </div>
          <div className="p-6 bg-primary/10 border border-primary/20 rounded-xl">
            <h4 className="font-headline font-bold text-foreground mb-2">Questions About Your Refund?</h4>
            <p className="text-sm text-muted-foreground font-body">
              Contact our support team via email for any clarifications regarding refunds or cancellations.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
